import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import PageHeader from "../shared/PageHeader";
import { getOrderById } from "../../services/order.api";
import { getStoredUser } from "../../services/auth.api";
import "./styles/invoice.css";

const fmt = (v = 0) => `৳${Number(v || 0).toLocaleString("en-BD")}`;

const fmtDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

/**
 * Routed as /invoice/:orderId. Everything here comes off the order the API
 * returns — the customer from the signed-in account, the line items and totals
 * from the order's own snapshot.
 */
export default function Invoice() {
  const { orderId } = useParams();
  const customer = getStoredUser();

  const { data, isPending, error } = useQuery({
    queryKey: ["invoice", orderId],
    queryFn: () => getOrderById(orderId),
    enabled: !!orderId,
  });

  const order = data?.data;

  const handlePrint = () => window.print();

  if (isPending) {
    return (
      <div className="container page-navbar-space">
        <PageHeader>Invoice</PageHeader>
        <div className="invoice-page mt-5 pb-5">
          <Skeleton height={520} borderRadius={14} />
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="container page-navbar-space">
        <PageHeader>Invoice</PageHeader>
        <div className="invoice-page mt-5 pb-5">
          <div
            style={{
              padding: 16,
              borderRadius: 12,
              background: "#fee2e2",
              color: "#991b1b",
              fontSize: 13,
              fontFamily: "'Inter', sans-serif",
            }}
          >
            We couldn't load this invoice.
          </div>
        </div>
      </div>
    );
  }

  const isPaid = order.paymentStatus === "paid";

  return (
    <div className="container page-navbar-space">
      <link
        href="https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=Nunito:wght@400;500;600;700&display=swap"
        rel="stylesheet"
      />
      <link
        href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.3/font/bootstrap-icons.css"
        rel="stylesheet"
      />

      <PageHeader>Invoice</PageHeader>

      <div className="invoice-page mt-5 pb-5">
        <div className="invoice-wrapper">
          <div className="invoice-card">
            <div className="invoice-header">
              <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
                <div className="invoice-brand">
                  <Link to="/" aria-label="Go to home" className="invoice-logo">
                    <span className="invoice-brand-mark">OF</span>
                  </Link>
                  <div>
                    <div className="invoice-brand-name">OwnFood</div>
                    <div className="invoice-brand-tagline">
                      Delivered with love · ownfood.com
                    </div>
                  </div>
                </div>

                <div className="d-flex flex-column align-items-end gap-2">
                  <span className="invoice-number-badge">
                    # {order.orderNumber}
                  </span>
                  <span
                    className={isPaid ? "invoice-status-paid" : "invoice-status-due"}
                  >
                    <i
                      className={
                        isPaid
                          ? "bi bi-check-circle-fill"
                          : "bi bi-exclamation-circle-fill"
                      }
                    ></i>{" "}
                    {isPaid ? "Paid" : "Payment due"}
                  </span>
                </div>
              </div>
            </div>

            <div className="invoice-meta">
              <div className="d-flex flex-wrap gap-4 align-items-start">
                <div className="flex-grow-1">
                  <div className="meta-block-label">Bill To</div>
                  <div className="meta-block-title">{customer?.name || "Customer"}</div>
                  <div className="meta-block-line">
                    {order.deliveryAddress?.phone || customer?.phone || ""}
                    <br />
                    {order.deliveryAddress?.address || ""}
                  </div>
                </div>

                <div className="meta-divider d-none d-md-block"></div>

                <div className="flex-grow-1">
                  <div className="meta-block-label">From</div>
                  <div className="meta-block-title">OwnFood</div>
                  <div className="meta-block-line">
                    support@ownfood.com
                    <br />
                    ownfood.com
                  </div>
                </div>

                <div className="meta-divider d-none d-md-block"></div>
                <div className="d-flex flex-column gap-2">
                  <div className="meta-date-box">
                    <div className="meta-date-label">Order Date</div>
                    <div className="meta-date-val">{fmtDate(order.createdAt)}</div>
                  </div>
                  <div className="meta-date-box">
                    <div className="meta-date-label">Delivered</div>
                    <div className="meta-date-val">{fmtDate(order.deliveredAt)}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="invoice-table-section">
              <h6>Order Items</h6>

              <table className="invoice-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th className="text-center">Qty</th>
                    <th className="text-end">Unit Price</th>
                    <th className="text-end">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {order.items.map((item, i) => (
                    <tr key={item._id || i}>
                      <td>
                        <div className="item-name">{item.name}</div>
                      </td>
                      <td className="text-center">
                        <span className="qty-badge">{item.quantity}</span>
                      </td>
                      <td className="text-end text-muted">{fmt(item.price)}</td>
                      <td className="text-end">{fmt(item.lineTotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="invoice-totals">
              <div className="totals-box">
                <div className="totals-row">
                  <span>Subtotal</span>
                  <span>{fmt(order.subTotal)}</span>
                </div>
                {order.discount > 0 && (
                  <div className="totals-row">
                    <span>
                      Discount{order.couponCode ? ` (${order.couponCode})` : ""}
                    </span>
                    <span className="discount">− {fmt(order.discount)}</span>
                  </div>
                )}
                <div className="totals-row">
                  <span>Delivery fee</span>
                  <span>{fmt(order.deliveryFee)}</span>
                </div>
                <div className="totals-row divider grand">
                  <span>Total</span>
                  <span>{fmt(order.total)}</span>
                </div>
                <div className="totals-row">
                  <span>Payment</span>
                  <span>
                    {order.paymentMethod === "cod"
                      ? "Cash on delivery"
                      : "Online payment"}
                  </span>
                </div>
              </div>
            </div>

            <div className="invoice-footer d-flex flex-wrap gap-2 justify-content-end">
              <Link to={`/order-details/${order._id}`} className="btn btn-outline-secondary btn-sm">
                Back to order
              </Link>
              <button type="button" onClick={handlePrint} className="btn btn-primary btn-sm">
                Print
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
